// Onboarding.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Onboarding.css';
import config from '../config';

const TOTAL_STEPS = 10;

function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // User identity & categorization
    userType: '',
    userOther: '', // for "Other" option in user type
    // Website scraping related fields
    websiteUrl: '',
    scrapedWebsiteInfo: '',
    // Existing fields
    twitterConnected: false,
    primaryGoal: '',
    customGoal: '',
    promotionType: '',
    audience: '',
    contentTypes: [],
    brandTone: '',
    brandValues: '',
    brandPersona: '',
    postingFrequency: '1',
    preferredTimes: [],
    inspirationHandles: ['', '', ''],
    competitorHandles: ['', ''],
    niche: '',
    customNiche: '',
    topicsToAvoid: '',
    complianceConcerns: '',
    knownBestTweet: '',
    knownWorstTweet: '',
    // New fields from previous updates
    automationPreference: '', // 'auto' or 'review'
    accountsManaged: '', // For agencies/in-house teams
    followerCount: '', // For individuals (optional)
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [websiteScrapeLoading, setWebsiteScrapeLoading] = useState(false);
  const [websiteScrapeError, setWebsiteScrapeError] = useState('');

  // Refs for focus management
  const stepHeadingRef = useRef(null);

  // On component mount, attempt to load saved data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('onboardingData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  // Auto-save formData to localStorage on every change
  useEffect(() => {
    localStorage.setItem('onboardingData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    // Move focus to the step heading whenever the currentStep changes
    if (stepHeadingRef.current) {
      stepHeadingRef.current.focus();
    }
  }, [currentStep]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === 'postingFrequency' ? String(value) : value,
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData((prev) => ({ ...prev, [field]: updatedArray }));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    // Validate current step if necessary (HTML5 required attributes help a bit)
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step: review was shown; now submit
      handleSubmit();
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Dummy function to simulate OAuth connection for Twitter
  const handleTwitterConnect = () => {
    // In a real app, this would trigger your OAuth flow.
    setFormData((prev) => ({ ...prev, twitterConnected: true }));
  };

  useEffect(() => {
    console.log('config.API_BASE_URL: ', config.API_BASE_URL);
  }, []);

  // Function to scrape a user's website
const handleWebsiteScrape = async () => {
  if (!formData.websiteUrl) return;
  
  setWebsiteScrapeLoading(true);
  setWebsiteScrapeError('');

  try {
    const response = await fetch(
      `${config.API_BASE_URL}/api/scrape?url=${encodeURIComponent(
        formData.websiteUrl
      )}`
    );

    if (!response.ok) {
      throw new Error(`Scraping failed with status ${response.status}`);
    }

    const data = await response.json();

    handleInputChange(
      'scrapedWebsiteInfo',
      JSON.stringify(data.info, null, 2)
    );
  } catch (error) {
    console.error('Scraping error:', error);

    let errorMessage = 'We couldn’t automatically fetch details from your website.';

    if (error.message.includes('Failed to fetch')) {
      errorMessage = "We're having trouble reaching your website. Please check the URL and try again.";
    } else if (error.message.includes('404')) {
      errorMessage = "The page couldn't be found. Please make sure the URL is correct.";
    } else if (error.message.includes('500')) {
      errorMessage = "Our servers are having trouble processing the request. Please try again later.";
    }

    setWebsiteScrapeError(
      `${errorMessage} You can still enter the details manually below.`
    );
  }

  setWebsiteScrapeLoading(false);
};

  const handleSubmit = async () => {
    // Process conditional fields
    const finalGoal =
      formData.primaryGoal === 'other'
        ? `Other: ${formData.customGoal}`
        : formData.primaryGoal;

    const finalNiche =
      formData.niche === 'other' ? formData.customNiche : formData.niche;

    const payload = {
      ...formData,
      primaryGoal: finalGoal,
      niche: finalNiche,
      // Filter out empty strings from arrays
      inspirationHandles: formData.inspirationHandles.filter(Boolean),
      competitorHandles:
        formData.userType !== 'individual'
          ? formData.competitorHandles.filter(Boolean)
          : [],
    };

    console.log('Submitting the following payload to /onboarding:', payload);

    try {
      const response = await fetch(`${config.API_BASE_URL}/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      console.log('Response status:', response.status);
      const responseData = await response.json().catch((e) => {
        console.error('Error parsing response JSON:', e);
        return null;
      });
      console.log('Response data:', responseData);

      if (response.ok) {
        // Clear saved data
        localStorage.removeItem('onboardingData');
        navigate('/dashboard');
      } else {
        console.error('Error onboarding:', responseData);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  // Render a simple progress bar
  const renderProgressBar = () => {
    const progressPercent = Math.round((currentStep / TOTAL_STEPS) * 100);
    return (
      <div className="progress-bar-container" aria-label="Progress">
        <div
          className="progress-bar"
          style={{ width: `${progressPercent}%` }}
        ></div>
        <p className="progress-text">{progressPercent}% complete</p>
      </div>
    );
  };

  // Final review component for Step 10
  const renderReview = () => {
    return (
      <div className="onboarding-review">
        <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
          Review Your Information
        </h3>
        <p>Please review your answers before submitting.</p>
        <pre className="review-summary">
          {JSON.stringify(formData, null, 2)}
        </pre>
      </div>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 1: Tell Us About Yourself
            </h3>
            <p className="help-text">
              This will help us tailor your experience. (Required)
            </p>
            <fieldset>
              <legend>Which of these best describes you?</legend>
              <div className="form-radio-group">
                <label htmlFor="individual">
                  <input
                    type="radio"
                    id="individual"
                    name="userType"
                    value="individual"
                    checked={formData.userType === 'individual'}
                    onChange={(e) =>
                      handleInputChange('userType', e.target.value)
                    }
                    required
                  />
                  Individual Content Creator / Solo Entrepreneur
                </label>
                <label htmlFor="smb">
                  <input
                    type="radio"
                    id="smb"
                    name="userType"
                    value="smb"
                    checked={formData.userType === 'smb'}
                    onChange={(e) =>
                      handleInputChange('userType', e.target.value)
                    }
                  />
                  Small/Medium Business or Startup
                </label>
                <label htmlFor="agency">
                  <input
                    type="radio"
                    id="agency"
                    name="userType"
                    value="agency"
                    checked={formData.userType === 'agency'}
                    onChange={(e) =>
                      handleInputChange('userType', e.target.value)
                    }
                  />
                  Freelance Social Media Manager / Small Agency
                </label>
                <label htmlFor="inhouse">
                  <input
                    type="radio"
                    id="inhouse"
                    name="userType"
                    value="inhouse"
                    checked={formData.userType === 'inhouse'}
                    onChange={(e) =>
                      handleInputChange('userType', e.target.value)
                    }
                  />
                  In-House Marketing Team
                </label>
                <label htmlFor="otherUserType">
                  <input
                    type="radio"
                    id="otherUserType"
                    name="userType"
                    value="other"
                    checked={formData.userType === 'other'}
                    onChange={(e) =>
                      handleInputChange('userType', e.target.value)
                    }
                  />
                  Other
                </label>
              </div>
            </fieldset>
            {formData.userType === 'other' && (
              <div className="form-group">
                <label htmlFor="userOther" className="form-label">
                  Please describe your role:
                </label>
                <input
                  type="text"
                  id="userOther"
                  className="form-input"
                  value={formData.userOther}
                  onChange={(e) =>
                    handleInputChange('userOther', e.target.value)
                  }
                  placeholder="e.g., Online educator, community manager, etc."
                  required
                />
              </div>
            )}
            <div className="website-info">
              <label htmlFor="websiteUrl" className="form-label">
                Your Website (Optional):
              </label>
              <input
                type="url"
                id="websiteUrl"
                className="form-input"
                value={formData.websiteUrl}
                onChange={(e) =>
                  handleInputChange('websiteUrl', e.target.value)
                }
                placeholder="https://example.com"
              />
              {formData.websiteUrl && (
                <button
                  type="button"
                  className="button-scrape"
                  onClick={handleWebsiteScrape}
                  disabled={websiteScrapeLoading}
                >
                  {websiteScrapeLoading ? 'Scraping...' : 'Scrape Website'}
                </button>
              )}
              {websiteScrapeError && (
                <p className="error-text">{websiteScrapeError}</p>
              )}
              <label htmlFor="scrapedWebsiteInfo" className="form-label">
                Website Info (editable):
              </label>
              <textarea
                id="scrapedWebsiteInfo"
                className="form-textarea"
                value={formData.scrapedWebsiteInfo}
                onChange={(e) =>
                  handleInputChange('scrapedWebsiteInfo', e.target.value)
                }
                placeholder="Scraped website info will appear here..."
              />
            </div>
            {/* Conditional fields for scale insights */}
            {formData.userType === 'individual' && (
              <div className="form-group">
                <label htmlFor="followerCount">
                  Current Follower Count (Optional):
                </label>
                <input
                  type="number"
                  id="followerCount"
                  className="form-input"
                  value={formData.followerCount}
                  onChange={(e) =>
                    handleInputChange('followerCount', e.target.value)
                  }
                  placeholder="e.g., 1500"
                />
                <p className="helper-text">
                  Enter the number of followers on your primary social media
                  account (e.g., your main Twitter account). This helps us
                  tailor recommendations based on your current reach. If you
                  have multiple accounts, please provide the count for the one
                  that best represents your personal brand.
                </p>
              </div>
            )}
            {(formData.userType === 'agency' ||
              formData.userType === 'inhouse') && (
              <div className="form-group">
                <label htmlFor="accountsManaged">
                  Number of Social Media Accounts Managed:
                </label>
                <input
                  type="number"
                  id="accountsManaged"
                  className="form-input"
                  value={formData.accountsManaged}
                  onChange={(e) =>
                    handleInputChange('accountsManaged', e.target.value)
                  }
                  placeholder="e.g., 3"
                  required
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 2: Connect Your Twitter Account
            </h3>
            <p className="help-text">
              Securely connect your Twitter account using OAuth. Your data will
              remain private.{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Learn more
              </a>
            </p>
            {!formData.twitterConnected ? (
              <button
                type="button"
                className="button-oauth"
                onClick={handleTwitterConnect}
              >
                Connect with Twitter
              </button>
            ) : (
              <p className="success-text">
                Twitter account connected successfully!
              </p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 3: Define Your Goal
            </h3>
            <p className="help-text">What is your primary goal? (Required)</p>
            <label htmlFor="primaryGoal" className="form-label">
              Primary Goal:
            </label>
            <select
              id="primaryGoal"
              className="form-select"
              value={formData.primaryGoal}
              onChange={(e) => handleInputChange('primaryGoal', e.target.value)}
              required
            >
              <option value="">Select...</option>
              <option value="growFollowers">
                Grow my social media following
              </option>
              <option value="promoteProduct">Promote a product/service</option>
              <option value="driveTraffic">Drive website traffic</option>
              <option value="buildCommunity">Build a community</option>
              <option value="other">Other</option>
            </select>
            {formData.primaryGoal === 'other' && (
              <>
                <label htmlFor="customGoal" className="form-label">
                  Please specify your goal:
                </label>
                <input
                  type="text"
                  id="customGoal"
                  className="form-input"
                  value={formData.customGoal}
                  onChange={(e) =>
                    handleInputChange('customGoal', e.target.value)
                  }
                  placeholder="Describe your specific goal"
                  required
                />
              </>
            )}
            <label htmlFor="promotionType" className="form-label">
              What are you promoting, if anything? (Optional)
            </label>
            <select
              id="promotionType"
              className="form-select"
              value={formData.promotionType}
              onChange={(e) =>
                handleInputChange('promotionType', e.target.value)
              }
            >
              <option value="">Select...</option>
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="affiliate">Affiliate links</option>
              <option value="blog">Blog, podcast, or book</option>
              <option value="none">Nothing specific</option>
            </select>
          </div>
        );

      case 4:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 4: Understand Your Audience
            </h3>
            <p className="help-text">
              Describe your target audience. (Required)
            </p>
            <label htmlFor="audience" className="form-label">
              Target Audience:
            </label>
            <textarea
              id="audience"
              className="form-textarea"
              value={formData.audience}
              onChange={(e) => handleInputChange('audience', e.target.value)}
              placeholder="Describe demographics, interests, etc."
              required
            />
          </div>
        );

      case 5:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 5: Define Content Preferences
            </h3>
            <p className="help-text">
              Choose the types of content you’d like to create.
            </p>
            <fieldset>
              <legend>
                What types of content do you want to create? (Optional)
              </legend>
              <p className="help-text">You can choose multiple options.</p>
              <div className="form-checkbox-group">
                <label htmlFor="informational">
                  <input
                    type="checkbox"
                    id="informational"
                    name="contentTypes"
                    checked={formData.contentTypes.includes('informational')}
                    onChange={(e) =>
                      handleInputChange(
                        'contentTypes',
                        e.target.checked
                          ? [...formData.contentTypes, 'informational']
                          : formData.contentTypes.filter(
                              (type) => type !== 'informational'
                            )
                      )
                    }
                  />
                  Informational/Educational posts
                </label>
                <label htmlFor="entertaining">
                  <input
                    type="checkbox"
                    id="entertaining"
                    name="contentTypes"
                    checked={formData.contentTypes.includes('entertaining')}
                    onChange={(e) =>
                      handleInputChange(
                        'contentTypes',
                        e.target.checked
                          ? [...formData.contentTypes, 'entertaining']
                          : formData.contentTypes.filter(
                              (type) => type !== 'entertaining'
                            )
                      )
                    }
                  />
                  Entertaining/Humorous content
                </label>
                <label htmlFor="promotional">
                  <input
                    type="checkbox"
                    id="promotional"
                    name="contentTypes"
                    checked={formData.contentTypes.includes('promotional')}
                    onChange={(e) =>
                      handleInputChange(
                        'contentTypes',
                        e.target.checked
                          ? [...formData.contentTypes, 'promotional']
                          : formData.contentTypes.filter(
                              (type) => type !== 'promotional'
                            )
                      )
                    }
                  />
                  Promotional posts
                </label>
              </div>
            </fieldset>
            <label htmlFor="brandTone" className="form-label">
              How would you describe your tone? (Required)
            </label>
            <input
              type="text"
              id="brandTone"
              className="form-input"
              value={formData.brandTone}
              onChange={(e) => handleInputChange('brandTone', e.target.value)}
              placeholder="e.g., witty, professional, friendly"
              required
            />
            <label htmlFor="brandValues" className="form-label">
              What are your core brand values or personality traits? (Optional)
            </label>
            <textarea
              id="brandValues"
              className="form-textarea"
              value={formData.brandValues}
              onChange={(e) => handleInputChange('brandValues', e.target.value)}
              placeholder="e.g., Innovation, authenticity, community"
            />
            <label htmlFor="brandPersona" className="form-label">
              Describe your brand’s persona in more detail: (Optional)
            </label>
            <textarea
              id="brandPersona"
              className="form-textarea"
              value={formData.brandPersona}
              onChange={(e) =>
                handleInputChange('brandPersona', e.target.value)
              }
              placeholder="e.g., A friendly guide on tech trends."
            />
          </div>
        );

      case 6:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 6: Posting Preferences
            </h3>
            <p className="help-text">
              Set how frequently and when you want to post. (Required)
            </p>
            <label htmlFor="postingFrequency" className="form-label">
              How often do you want to post?
            </label>
            <div className="form-slider">
              <input
                type="range"
                id="postingFrequency"
                min="1"
                max="30"
                value={formData.postingFrequency || 1}
                onChange={(e) =>
                  handleInputChange('postingFrequency', e.target.value)
                }
                required
              />
              <span className="slider-value">
                {formData.postingFrequency || 1} times per day
              </span>
            </div>
            <fieldset>
              <legend>Preferred posting times (Optional)</legend>
              <p className="help-text">Select all that apply.</p>
              {[
                'Early Morning (6-9 AM)',
                'Morning (9-12 PM)',
                'Afternoon (12-3 PM)',
                'Evening (3-6 PM)',
                'Late Evening (6-9 PM)',
              ].map((time) => (
                <label key={time} className="toggle-label" htmlFor={time}>
                  <input
                    type="checkbox"
                    id={time}
                    name="preferredTimes"
                    checked={formData.preferredTimes.includes(time)}
                    onChange={() => {
                      const updatedTimes = formData.preferredTimes.includes(
                        time
                      )
                        ? formData.preferredTimes.filter((t) => t !== time)
                        : [...formData.preferredTimes, time];
                      handleInputChange('preferredTimes', updatedTimes);
                    }}
                  />
                  {time}
                </label>
              ))}
            </fieldset>
            <fieldset>
              <legend>Posting Mode (Required):</legend>
              <p className="help-text">
                Would you like to review posts before they go live, or allow the
                AI to post automatically?
              </p>
              <label htmlFor="reviewMode">
                <input
                  type="radio"
                  id="reviewMode"
                  name="automationPreference"
                  value="review"
                  checked={formData.automationPreference === 'review'}
                  onChange={(e) =>
                    handleInputChange('automationPreference', e.target.value)
                  }
                  required
                />
                Review before posting
              </label>
              <label htmlFor="autoMode">
                <input
                  type="radio"
                  id="autoMode"
                  name="automationPreference"
                  value="auto"
                  checked={formData.automationPreference === 'auto'}
                  onChange={(e) =>
                    handleInputChange('automationPreference', e.target.value)
                  }
                  required
                />
                Fully automated posting
              </label>
            </fieldset>
          </div>
        );

      case 7:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 7: Inspiration & Guidelines
            </h3>
            <p className="help-text">
              List any accounts you admire (up to 3) and – if applicable – your
              main competitors.
            </p>
            <label className="form-label" htmlFor="inspiration1">
              Inspirational accounts (Optional):
            </label>
            <div className="form-input-group">
              {formData.inspirationHandles.map((handle, idx) => (
                <input
                  key={idx}
                  type="text"
                  id={`inspiration${idx}`}
                  className="form-input"
                  value={handle}
                  onChange={(e) =>
                    handleArrayChange('inspirationHandles', idx, e.target.value)
                  }
                  placeholder={`@handle ${idx + 1}`}
                />
              ))}
            </div>
            {formData.userType !== 'individual' && (
              <>
                <label className="form-label" htmlFor="competitor1">
                  Competitor or industry leader accounts (Optional):
                </label>
                <div className="form-input-group">
                  {formData.competitorHandles.map((handle, idx) => (
                    <input
                      key={idx}
                      type="text"
                      id={`competitor${idx}`}
                      className="form-input"
                      value={handle}
                      onChange={(e) =>
                        handleArrayChange(
                          'competitorHandles',
                          idx,
                          e.target.value
                        )
                      }
                      placeholder={`@competitor ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 8:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 8: Niche & Account Setup
            </h3>
            <p className="help-text">
              Define your niche or industry. (Required)
            </p>
            <label htmlFor="niche" className="form-label">
              What is your niche or industry?
            </label>
            <select
              className="form-select"
              id="niche"
              value={formData.niche}
              onChange={(e) => handleInputChange('niche', e.target.value)}
              required
            >
              <option value="">Select...</option>
              <option value="technology">Technology</option>
              <option value="finance">Finance</option>
              <option value="health">Health & Wellness</option>
              <option value="marketing">Marketing</option>
              <option value="ai">AI / Machine Learning</option>
              <option value="other">Other</option>
            </select>
            {formData.niche === 'other' && (
              <>
                <label htmlFor="customNiche" className="form-label">
                  Please specify your niche:
                </label>
                <input
                  type="text"
                  className="form-input"
                  id="customNiche"
                  value={formData.customNiche}
                  onChange={(e) =>
                    handleInputChange('customNiche', e.target.value)
                  }
                  placeholder="Describe your niche"
                  required
                />
              </>
            )}
          </div>
        );

      case 9:
        return (
          <div className="onboarding-step">
            <h3 ref={stepHeadingRef} tabIndex="-1" className="step-title">
              Step 9: Constraints & Past Performance
            </h3>
            <p className="help-text">
              Let us know about any topics to avoid or examples of past posts.
            </p>
            <label htmlFor="topicsToAvoid" className="form-label">
              Sensitive topics or words to avoid (Optional):
            </label>
            <textarea
              className="form-textarea"
              id="topicsToAvoid"
              value={formData.topicsToAvoid}
              onChange={(e) =>
                handleInputChange('topicsToAvoid', e.target.value)
              }
              placeholder="List any content guidelines or taboo topics."
            />
            <label htmlFor="complianceConcerns" className="form-label">
              Compliance or regulatory concerns (Optional):
            </label>
            <textarea
              className="form-textarea"
              id="complianceConcerns"
              value={formData.complianceConcerns}
              onChange={(e) =>
                handleInputChange('complianceConcerns', e.target.value)
              }
              placeholder="e.g., disclaimers for financial advice"
            />
            <label htmlFor="knownBestTweet" className="form-label">
              Link or describe a tweet that performed well (Optional):
            </label>
            <input
              type="text"
              className="form-input"
              id="knownBestTweet"
              value={formData.knownBestTweet}
              onChange={(e) =>
                handleInputChange('knownBestTweet', e.target.value)
              }
              placeholder="URL or description of your best tweet"
            />
            <label htmlFor="knownWorstTweet" className="form-label">
              Link or describe a tweet that underperformed (Optional):
            </label>
            <input
              type="text"
              className="form-input"
              id="knownWorstTweet"
              value={formData.knownWorstTweet}
              onChange={(e) =>
                handleInputChange('knownWorstTweet', e.target.value)
              }
              placeholder="URL or description of your worst tweet"
            />
          </div>
        );

      case 10:
        return renderReview();

      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      <h2 className="onboarding-title" id="onboardingHeading">
        Onboarding
      </h2>
      <p className="onboarding-description">
        Let’s get to know your needs so we can tailor your IntenceAI experience.
      </p>
      {/* Progress Bar */}
      {renderProgressBar()}
      <form
        className="onboarding-form"
        onSubmit={handleNextStep}
        aria-labelledby="onboardingHeading"
      >
        {renderStep()}
        <div className="onboarding-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              className="button-back"
              onClick={handlePreviousStep}
            >
              Back
            </button>
          )}
          <button type="submit" className="button-next">
            {currentStep < TOTAL_STEPS ? 'Next' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Onboarding;
